import React, { useState, useContext, useEffect, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import appConfig from 'app-config';

import metadata from './MLDocumentsUploadPopup.metadata.json5';
import styles from './GatewayMLDocumentsUploadPopup.module.scss';
import messages from './MLDocumentsUploadPopup.messages'

const MLDocumentsUploadPopup = (props) => {
    const {
        policyNumber,
        policy,
        onCancel,
        viewModelService,
        authHeader,
        contingencyId,
        contingencyName,
        docSubtype,
        isFromPolicyChangeScreen,
        setIsFileUploadDone,
        contingency,
        refreshPreBindContingencyData,
        refreshOpenOrClosedPreBindContingencies,
        setcontingencies,
        portalSettings
    } = props;

    const { refreshData } = useDataRefresh();
    const translator = useTranslator();

    const { onValidate, isComponentValid } = useValidation('MLDocumentsUploadPopup');
    const { unacceptableFileExt, maxFileUploadSize } = appConfig
    const [documentVM, updateDocumentVM] = useState({});
    const [documentSubTypes, updateDocumentSubTypes] = useState([]);

    const [documentData, updateDocumentData] = useState({});
    const documentSubtypeTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('DocumentSubType_ML');

    const [errorMessage, setErrorMessage] = useState('');
    const [isFailUploadFile, setIsFailUploadFile] = useState(false);
    const [isOtherOption, setIsOtherOption] = useState(false);
    const [successfulUpload, setSuccessfulUpload] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [contingencyDisplayName, setContingencyDisplayName] = useState('');
    //US993539
    const  [subtypeNameElementTitle, updateDocumentSubtypeName] = useState([]);

    const RECURRING_CC_AUTHORIZATION = 'recurring_credit_or_debit_card_authorization';
    const RECURRING_EFT_AUTHORIZATION = 'recurring_eft_authorization';

    const getFormContingenciesDocSubType = (contingencies) => {
        return contingencies?.filter((contingency) => contingency.docSubType_ML !== RECURRING_CC_AUTHORIZATION
            && contingency.docSubType_ML !== RECURRING_EFT_AUTHORIZATION)
            .map((contingency) => contingency.docSubType_ML);
    };

    const getFinalFilteredSubTypes = (subtypes) => {
        let updatedSubTypes = [...subtypes];

        if (portalSettings && portalSettings.enableEFT_CCDocSubtypeUIFiltering_Ext) {
            const deprecatedSubtypes = [RECURRING_CC_AUTHORIZATION, RECURRING_EFT_AUTHORIZATION];
            const hasOpenDeprecatedContingency = policy?.openContingencies_Ext?.some(con => deprecatedSubtypes.includes(con.docSubType_ML));

            if (!hasOpenDeprecatedContingency) {
                updatedSubTypes = updatedSubTypes.filter(
                    subtype => !deprecatedSubtypes.includes(subtype.code)
                );
            }
        }

        // FYI: Apply future subtype filtering rules here before returning the final list.
        return updatedSubTypes;
    };



    useEffect(() => {
        try {
            const typeKeyName = viewModelService.productMetadata
                .get('pc')
                .types.getTypelist('ContingencyName_Ext')
                .getCode(contingencyName).name;
            const displayName = translator({id: typeKeyName, defaultMessage: typeKeyName})
            setContingencyDisplayName(displayName);
        } catch (e) {
            setContingencyDisplayName(contingencyName || '');
        }
    }, [contingencyName, refreshData, translator, viewModelService]);

    const defineDocSubtype = (policyData) => {
        let productName = policyData?.product?.productName
        if(productName == undefined){
            productName = policyData?.baseData?.productName;
        }
        switch (productName) {
            case 'Personal Auto':
                return 'dex_doc_type_auto';
            case 'Homeowners':
                return 'dex_doc_type_home';
            default:
                return 'dex_doc_type_common';
        }
    }

    const initDocument = useCallback(
        () => {
            return {
                policyNumber,
                contingencyFormSubTypes_ML: getFormContingenciesDocSubType(policy?.formContingencies_ML),
                documentSubTypeFilterName_ML: defineDocSubtype(policy),
            }
        },
        [policy, policyNumber]
    );

    const createVM = useCallback(
        (model) => {
            return viewModelService.create(
                model,
                'pc',
                'edge.capabilities.policy.document.dto.PolicyDocumentUploadDTO'
            );
        },
        [viewModelService]
    );

    const initDocSubTypeFilterValue = (docSubtypeTypelist, docfilterType) => {
        return _.find(docSubtypeTypelist.filters,
            (documentSubtypeFilter) => {
                return documentSubtypeFilter.name === docfilterType
            }).codes
    }

    const getContingenciesDocSubTypeFilterValue = (contdocSubtypeTypelist, contdocfilterType) => {
        return contdocSubtypeTypelist?.codes.filter((item) => contdocfilterType?.includes(item.code));

    }

    const getCombinedDocSubTypeFilterValue = (contdocSubtypeTypelist, contdocfilterType) => {
        return _.sortBy([...new Set(contdocSubtypeTypelist.concat(contdocfilterType))], 'code');
    }
    //US993539
    const filterWaterSensorDocs = (docs) =>
        docs.filter((v) => v.code === contingency.docSubType_ML);
    
    const initializeDocVM = useCallback(
        () => {
            const doc = createVM(initDocument());
            if (docSubtype) {
                _.set(doc, 'documentSubType_ML', docSubtype);
            }
            updateDocumentVM(doc);
            const lobAlwaysDocumentSubTypes = (initDocSubTypeFilterValue(documentSubtypeTypelist, defineDocSubtype(policy)));
            const contingenciesDocumentSubTypes = (getContingenciesDocSubTypeFilterValue(documentSubtypeTypelist, getFormContingenciesDocSubType(policy?.formContingencies_ML)));
            const lobAlwaysAndContingenciesDocSubTypes = (getCombinedDocSubTypeFilterValue(lobAlwaysDocumentSubTypes, contingenciesDocumentSubTypes));
            const finalDocSubTypes = getFinalFilteredSubTypes(lobAlwaysAndContingenciesDocSubTypes)
            
            //US993539
            if(contingency){
                let filterWaterSensor = filterWaterSensorDocs(finalDocSubTypes)
                if(filterWaterSensor.length>0){
                    doc.documentSubType_ML.value = filterWaterSensor[0];
                    updateDocumentSubTypes( filterWaterSensor);
                }
                let documentSubtypeNameTitle = translator({
                    id: doc?.documentSubType_ML?.value?.name,
                    defaultMessage: doc?.documentSubType_ML?.value?.name
                })
                updateDocumentSubtypeName(documentSubtypeNameTitle);
            }
            else
                updateDocumentSubTypes(finalDocSubTypes)
           
        }, [createVM, initDocument, docSubtype, documentSubtypeTypelist, policy]
    );

    useEffect(() => {
        initializeDocVM();
    }, [initializeDocVM, translator]);

    const handleChange = useCallback(
        (value, path) => {
            const newDocumentSubmissionVM = viewModelService.clone(documentVM);
            _.set(newDocumentSubmissionVM.value, path, value !== '' ? value : undefined);
            switch (path) {
                case 'type':
                    _.set(newDocumentSubmissionVM, 'type', value);
                    break;
                case 'documentSubType_ML':
                    _.set(newDocumentSubmissionVM, 'documentSubType_ML', value);
                    value ? setIsOtherOption(value.includes('other')) : setIsOtherOption(false) 
                    break;
                default:
                    break;
            }
            updateDocumentVM(newDocumentSubmissionVM);
            refreshData();
        },
        [documentVM, refreshData, viewModelService]
    );

    const onUploadDocument = useCallback(
        (file) => {
            updateDocumentData(file)
        },
        []
    );

    const onDocumentChange = () => {
        if (documentData) {
            updateDocumentData({});
        }
    }

    const transformAvailableValues = (values) => {
        return values?.map((value) => {
            return {
                code: value.code,
                name: translator({
                    id: value.name,
                    defaultMessage: value?.name
                })
            }
        })
    }

    const handleSave = async () => {
        if (!isComponentValid) {
            setShowErrors(true);
            return;        
        }

        if (!(documentData && documentData.name && documentData.type) || isUploading) {
            return;
        }
    
        const documentUploadTokenId = await GatewayDocumentService.generateUploadToken(authHeader);

        const fileName = documentData.name;
        const tmpArray = fileName.split('.');
        let fileExt = {};

        if (tmpArray.length > 0) {
            fileExt = tmpArray[tmpArray.length - 1];
        }
        const acceptableFileExt = fileExt && !unacceptableFileExt.includes(fileExt);
        const fileSizeValid = documentData.size <= maxFileUploadSize;

        const contingencySubtypes = _.get(documentVM.value,'contingencyFormSubTypes_ML');
        if (docSubtype !== null) {
            if (_.isArray(contingencySubtypes) && !contingencySubtypes.includes(docSubtype)) {
                contingencySubtypes.push(docSubtype);
            }
        }

        if (acceptableFileExt && fileSizeValid) {
            const newDocumentMetadata = {
                policyNumber,
                xCenter: 'pc',
                uploadingDoc: 'gpa/upload',
                documentType: _.get(documentVM.value, 'type'),
                ContingencyFormSubTypes_ML: contingencySubtypes,
                DocumentSubTypeFilterName_ML: _.get(documentVM.value,'documentSubTypeFilterName_ML'),
                DocumentSubType_ML: _.get(documentVM.value, 'documentSubType_ML'),
                contingencyId_ML: contingencyId,
                name: documentData.name,
                mimeType: documentData.type,
                sessionID: documentUploadTokenId,
                status: 'approved',
            }

            try {
                setIsUploading(true)
                await GatewayDocumentService.uploadDocument(documentData, newDocumentMetadata, authHeader)
                setSuccessfulUpload(true);
                initializeDocVM();
                updateDocumentData({});
                setIsFailUploadFile(false);

                if(refreshOpenOrClosedPreBindContingencies){
                    refreshOpenOrClosedPreBindContingencies();
                }

                //US993539
                if(isFromPolicyChangeScreen){
                    if (Array.isArray(policy?.preBindContingenciesToDTO)) {
                        policy.preBindContingenciesToDTO = []; 
                        if(setcontingencies !== undefined){
                            setcontingencies([]);
                        }
                    }
                    if (typeof setIsFileUploadDone === 'function') {
                        setIsFileUploadDone(false);
                    }
                }
            } catch (error) {
                setErrorMessage(translator(messages.documentUploadFail, { fileName: fileName }))
                setIsFailUploadFile(true)
                //US993539
                if(isFromPolicyChangeScreen){
                    if (typeof setIsFileUploadDone === 'function') {
                        setIsFileUploadDone(true);
                    }
                }
            } finally {
                setIsUploading(false);
            }

        } else {
            setErrorMessage(acceptableFileExt ? messages.invalidFileSize : messages.invalidFileType)
            setIsFailUploadFile(true)
        }
    }

    const handleDrag = (event) => {
        event.preventDefault();
        event.stopPropagation();
    }

    const handleDrop = (event) => {
        event.preventDefault();
        const fileName = event.target.value || event.dataTransfer.files[0].name;
        console.info(`Dragged ${fileName}`);
        const file = event.dataTransfer ? event.dataTransfer.files[0] : event.target.files[0];
        onUploadDocument(file);
    }

    const showSubtypesMessage = () => {
        return (subtypeNameElementTitle  !== undefined  && isFromPolicyChangeScreen)? true : false;
    };

    const overrideProps = {
        uploadPopupHeader: {
            className: styles.modalHeader
        },
        uploadPopupBody: {
            className: styles.modalBody
        },
        fieldGrid: {
            className: !isFromPolicyChangeScreen ? styles.fieldGrid : ''
        },
        alertGroupPopup: {
            className: `${styles.alertNotificationStyling} vertical-spacing-30`
        },
        warningInlineNotification: {
            message: translator(messages.whenYouHaveMoreThanOneDocument)
        },
        successInlineNotification: {
            message: translator(messages.documentUploadedSuccessfully, {
                strong: (chunks) => <strong>{chunks}</strong>
            }),
            visible: successfulUpload
        },
        errorInlineNotification: {
            message: errorMessage,
            visible: isFailUploadFile
        },
        type: {
            availableValues: [{ name: 'Policy Document', code: 'Policy Document' }],
            value: _.get(documentVM.value, 'type'),
            visible: contingencyDisplayName === ''
        },
        contingencySubHeader:{
            visible:contingencyDisplayName !== '' && isFromPolicyChangeScreen === true ,
        },
        contingencyNameElement: {
            visible: contingencyDisplayName !== '',
            content: contingencyDisplayName
        },
        SubtypeHeader:{
            visible:showSubtypesMessage()
        },
        SubtypeNameElement:{
            content: subtypeNameElementTitle,
            visible:showSubtypesMessage()
        },
        subtype: {
            availableValues: transformAvailableValues(documentSubTypes),
            visible:subtypeNameElementTitle == ''
        },
        subtypeContainer: {
            visible: !docSubtype
        },
        uploadDocumentsDiv: {
            onDragOver: handleDrag,
            onDrop: handleDrop
        },
        upload: {
            buttonType: "outlined"
        },
        uploadedFileNameContainer: {
            visible: !_.isUndefined(documentData),
            content: documentData.name
        },
        policy: {
            visible: false
        },
        closeButton: {
            content: translator(messages.cancelModal),
            className: styles.cancelBtn
        },
        saveButton: {
            content: translator(messages.continue)
        }
    };

    const resolvers = {
        resolveComponentMap: {
            MLAlertGroupComponent
        },
        resolveCallbackMap: {
            onClose: onCancel,
            onSave: handleSave,
            onUploadDocument: onUploadDocument,
            onDocumentChange: onDocumentChange
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={documentVM}
            onValueChange={handleChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
            onValidationChange={onValidate}
            showErrors={showErrors}
        />
    );
}


MLDocumentsUploadPopup.propTypes = {
    policy: PropTypes.shape({}),
    policyNumber: PropTypes.string.isRequired,
    contingencyId: PropTypes.string,
    contingencyName: PropTypes.string
}

export default MLDocumentsUploadPopup;
